import * as React from 'react';
import styles from './AlertHeader.module.css';
import { Panel, Avatar, useAlertBackground, BatteryIndicator } from '@ixicare/ui';
import { t } from '~/i18n';
import Alert from '~store/Alert/Alert';
import { useQuery } from '@tanstack/react-query';
import { Device, DeviceType, fetchLivingUnit, Profile } from '@ixicare/resources';
import { Alert as AlertBox } from '@ixicare/ui';

interface AlertHeaderProps {
  alert: Alert;
}

const AlertHeader: React.FC<AlertHeaderProps> = ({ alert }) => {
  const alarmGradientStyle = useAlertBackground(alert.priority);
  const { data: resident } = useQuery({
    queryKey: ['profile', alert.resident?.id],
    queryFn: () => Profile.getProfile(alert.resident?.id ?? -1),
    enabled: !!alert.resident,
  });
  const { data: livingUnit } = useQuery({
    queryKey: ['livingUnit', alert.resident?.id],
    queryFn: () => fetchLivingUnit(resident?.livingUnitId ?? ''),
    enabled: !!resident?.livingUnitId,
  });
  const { data: device } = useQuery({
    queryKey: ['device', alert.device?.id],
    queryFn: () => Device.getDevice(alert.device?.id ?? -1),
    enabled: !!alert.device,
  });

  const deviceImage = device?.deviceType ? `/assets/${device.deviceType.toLowerCase()}@2x.png` : undefined;
  const showDeviceImage = !alert.resident;

  return (
    <>
      <Panel className={styles.Panel}>
        {alert.device && (
          <div className={styles.AvatarWrapper}>
            <Avatar
              style={
                showDeviceImage
                  ? {
                      backgroundSize: '80%',
                      backgroundRepeat: 'no-repeat',
                    }
                  : undefined
              }
              src={showDeviceImage ? deviceImage : undefined}
              className={styles.Avatar}
            />
          </div>
        )}
        <div className={styles.AlertInfo} style={alarmGradientStyle}>
          <div
            style={{
              padding: '0 30px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>
              {alert.resident
                ? t('profile.healthLevel') + ': ' + (resident?.healthLevel ?? resident?.katzLevel ?? '-')
                : ''}
            </span>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              {!showDeviceImage && deviceImage && (
                <img style={{ height: 20, width: 20, marginRight: 5 }} src={deviceImage} />
              )}
              {typeof device?.batteryLevel === 'number' && (
                <BatteryIndicator
                  color="white"
                  inaccurate={device.deviceType !== DeviceType.WATCHERR}
                  percentage={device.batteryLevel}
                />
              )}
            </div>
          </div>
          <div className={styles.AlarmLabel}>
            <i className="icon-alarm"></i>
            <h2> {t(`alert.types.${alert.eventType.toLowerCase()}`)}</h2>
          </div>
          <h1>{alert.description}</h1>
          <h2>{livingUnit ? `${livingUnit?.name} - ${resident?.site?.name}` : (resident?.site?.name ?? '')}</h2>
          {alert.locationDescription && <h2>{alert.locationDescription}</h2>}
        </div>
      </Panel>
      {!alert.resident &&
        (device?.deviceType === DeviceType.WRISTBAND_DISTRESS_BUTTON || device?.deviceType === DeviceType.WATCHERR) && (
          <div className="mt-10">
            <AlertBox type="error">
              {t('alert.detail.noDeviceOwnerWarning', { mac: device?.macAddress ?? '-' })}
            </AlertBox>
          </div>
        )}
    </>
  );
};

export default AlertHeader;
